/**
 * @fileoverview Component for displaying a search bar.
 * This component includes a search input and a clear button.
 */

/* eslint-disable jsx-a11y/no-autofocus */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ClearIcon from '../../assets/clear.svg'
import MagnifyingGlassIcon from '../../assets/magnifying-glass.svg'
import { debounce } from '../../utils/debounce'

/**
 * @interface SearchProps
 * @description Props for the Search component.
 * @property {string} placeholder - The placeholder text for the search input.
 */
interface SearchProps {
    placeholder?: string
}

/**
 * @function Search
 * @description Component for displaying a search bar.
 * @param {SearchProps} props - The props for the component.
 * @returns {JSX.Element} - A React component that renders the search bar.
 */
export function Search(props: SearchProps) {
    const sp = new URLSearchParams(location.search)
    const hasSearchString = sp.get('search') !== '' && sp.get('search') !== null

    const [isExpanded, setIsExpanded] = useState(hasSearchString)
    const navigate = useNavigate()
    const ref = useRef<HTMLInputElement>(null)

    const onChange = useMemo(
        () =>
            debounce((value: string) => {
                if (ref.current) {
                    ref.current.value = value
                    ref.current.focus()
                }
                if (value !== '') {
                    sp.set('search', value)
                    sp.delete('offset')
                } else {
                    sp.delete('search')
                    sp.delete('offset')
                    sp.delete('limit')
                }
                navigate(`${location.pathname}?${sp.toString()}`, { replace: true })
            }),
        []
    )

    useEffect(() => {
        if (ref.current) {
            ref.current.value = sp.get('search') || ''
        }
    }, [ref.current, location.search])

    useEffect(() => {
        function handleMouseDown(e: MouseEvent) {
            // We want to prevent any focus shifts which cause flickering if the event target is within the component
            if (ref.current?.parentElement.contains(e.target as Node) && e.target !== ref.current) {
                e.preventDefault()
            }

            // If the user clicks outside the component, we want to collapse it
            // unless the user is typing in the search field
            if (
                ref.current &&
                !ref.current.parentElement.contains(e.target as Node) &&
                ref.current.value === ''
            ) {
                setIsExpanded(false)
            }
        }

        // Double click is used to select text, so we want to prevent the component from collapsing
        document.addEventListener('dblclick', handleMouseDown)
        document.addEventListener('mousedown', handleMouseDown)
        return () => {
            document.removeEventListener('mousedown', handleMouseDown)
            document.removeEventListener('dblclick', handleMouseDown)
        }
    }, [])

    return (
        <div className="flex">
            {!isExpanded ? (
                <button
                    type="button"
                    className="flex shrink-0 p-2 items-center w-10 h-10 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={() => setIsExpanded(true)}
                >
                    <MagnifyingGlassIcon className="w-5 h-5 pl-0.5" />
                </button>
            ) : (
                <div className="relative">
                    <div className="absolute top-0 left-0 flex items-center h-full">
                        <label htmlFor="search">
                            <MagnifyingGlassIcon className="w-5 h-5 ml-3" />
                        </label>
                    </div>
                    <div className="absolute top-0 right-0 flex items-center h-full">
                        <button className="p-3 cursor-pointer" onClick={() => onChange('')}>
                            <ClearIcon className="w-5 h-5" />
                        </button>
                    </div>
                    <input
                        className="w-80 h-10 rounded-md pl-10 border-gray-200 focus:ring-indigo-500 search-cancel:hidden placeholder:text-sm placeholder:text-gray-500 placeholder:pb-1"
                        ref={ref}
                        name="search"
                        id="search"
                        autoFocus
                        type="search"
                        placeholder={props.placeholder}
                        onBlur={(e) => {
                            if (ref.current?.value === '' && e.target === document.activeElement) {
                                setIsExpanded(false)
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Tab' && ref.current?.value === '') {
                                setIsExpanded(false)
                            }
                        }}
                        onChange={(e) => {
                            onChange(e.target.value)
                        }}
                    />
                </div>
            )}
        </div>
    )
}
